import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import DamagedPartTile, { DamagedPartCodes, MapBase, ThemeStyleInfo } from './DamagePickerTile';

export interface DamagePartTileInteraction {
  themeInfo: ThemeStyleInfo;
  isMobile: boolean;
  tileClickHandler: (damageCode: string) => void;
  currentSelectionStateRequestHandler: (damageCode: string) => boolean;
}

export interface DamagePickerInfo {
  selectedDamageCodes: string[];
  themeInfo: ThemeStyleInfo;
  selectionChangeHandler: (damageCode: string[]) => void;
  isMobile: boolean;
}

export default function DamagePicker(props: DamagePickerInfo) {
  /*High level logic*/
  /*
  Single state is maintained to track the selected damage codes. 
  If already selected codes are passed, the state will get updated to reflect it.
  A change in state will trigger a handler function of parent via 'useEffect' hook.
  'currentSelectionStateRequestHandler' function is used to check the selection status of each damage code.
  */
  const [selectedDamageCodes, setSelectedDamageCodes] = useState<string[]>(props.selectedDamageCodes);
  const themeStyleInfo: ThemeStyleInfo = props.themeInfo;
  const border = `1px solid ${themeStyleInfo.outLineStyle.fillColor}`;

  // This is to be executed when the state changes so that it updates the parent
  const { selectionChangeHandler } = props;
  useEffect(() => {
    selectionChangeHandler(selectedDamageCodes);
  }, [selectionChangeHandler, selectedDamageCodes]);

  const updateLocalStateOnSelectedDamageCode = useCallback(
    (damageCode: string) => {
      if (!selectedDamageCodes.includes(damageCode)) {
        setSelectedDamageCodes([...selectedDamageCodes, damageCode]);
      } else {
        // assigning the list to temp variable
        const temp = [...selectedDamageCodes];
        // removing the element using splice
        temp.splice(selectedDamageCodes.indexOf(damageCode), 1);
        // updating the list
        setSelectedDamageCodes(temp);
      }
    },
    [selectedDamageCodes]
  );

  const currentSelectionStateRequestHandler = (damageCode: string) => {
    if (selectedDamageCodes) {
      const isSelected = selectedDamageCodes.includes(damageCode);
      return isSelected;
    } else {
      return false;
    }
  };

  function handleTileClickEvent(code: string) {
    // Update local state with selected 'option Ids'
    updateLocalStateOnSelectedDamageCode(code);
  }

  return (
    <Stack alignItems="center" justifyContent="center">
      <Box
        sx={{
          flexGrow: 1,
          minWidth: '450px',
          maxWidth: '600px',
          marginTop: '2rem',
        }}
      >
        <Grid container item spacing={0} direction="row" xs={12} justifyContent="center" alignItems="center">
          <Grid item xs={10}>
            <Grid
              container
              item
              spacing={0}
              direction="column"
              xs={12}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={12}>
                <Typography align="center">Front</Typography>
              </Grid>
              <Grid item container xs={12}>
                <Grid item xs={1} justifyContent="center" alignItems="center" container>
                  <Typography
                    sx={{
                      transform: 'rotate(-90deg)',
                      marginRight: '-.8rem',
                    }}
                  >
                    Driver
                  </Typography>
                </Grid>
                <Grid item container xs={10} justifyContent="center" alignItems="center">
                  <Paper
                    variant="outlined"
                    square
                    sx={{
                      border: border,
                      pb: '.5rem',
                      width: '100%',
                    }}
                  >
                    <Grid
                      container
                      item
                      spacing={0}
                      direction="row"
                      xs={12}
                      justifyContent="center"
                      alignItems="stretch"
                      // sx={{ p: '.1rem' }}
                    >
                      <DamageMapLeftSection
                        themeInfo={props.themeInfo}
                        tileClickHandler={handleTileClickEvent}
                        currentSelectionStateRequestHandler={currentSelectionStateRequestHandler}
                        isMobile={props.isMobile}
                      />
                      <DamageMapCenterSection
                        themeInfo={props.themeInfo}
                        tileClickHandler={handleTileClickEvent}
                        currentSelectionStateRequestHandler={currentSelectionStateRequestHandler}
                        isMobile={props.isMobile}
                      />
                      <DamageMapRightSection
                        themeInfo={props.themeInfo}
                        tileClickHandler={handleTileClickEvent}
                        currentSelectionStateRequestHandler={currentSelectionStateRequestHandler}
                        isMobile={props.isMobile}
                      />
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item container xs={1} justifyContent="center" alignItems="center">
                  <Typography
                    sx={{
                      transform: 'rotate(-270deg)',
                      marginRight: '.8rem',
                    }}
                  >
                    Passenger
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography align="center">Rear</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}

function DamageMapLeftSection(props: DamagePartTileInteraction) {
  return (
    <Grid item container direction="column" spacing={3} xs={3} alignItems="center">
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.LEFT_FRONT_WHEEL)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.LEFT_FRONT_WHEEL);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.LEFT_FRONT_WHEEL}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.LEFT_FRONT_WHEEL)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.LEFT_FENDER)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.LEFT_FENDER);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.LEFT_FENDER}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.LEFT_FENDER)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.LEFT_FRONT_DOOR)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.LEFT_FRONT_DOOR);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.LEFT_FRONT_DOOR}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.LEFT_FRONT_DOOR)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>

      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.LEFT_REAR_DOOR)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.LEFT_REAR_DOOR);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.LEFT_REAR_DOOR}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.LEFT_REAR_DOOR)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.LEFT_QUARTER_PANEL)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.LEFT_QUARTER_PANEL);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.LEFT_QUARTER_PANEL}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.LEFT_QUARTER_PANEL)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.LEFT_REAR_WHEEL)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.LEFT_REAR_WHEEL);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.LEFT_REAR_WHEEL}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.LEFT_REAR_WHEEL)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
    </Grid>
  );
}

function DamageMapCenterSection(props: DamagePartTileInteraction) {
  return (
    <Grid
      container
      item
      direction="column"
      justifyContent="space-evenly"
      alignItems="center"
      id="gc3"
      xs={6}
      spacing={1}
    >
      <Grid
        item
        sx={{
          position: 'relative',
        }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.FRONT_BUMPER)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.FRONT_BUMPER);
          }
        }}
        id="top-center"
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.FRONT_BUMPER}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.FRONT_BUMPER)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid container id="center" justifyContent="center">
        <Grid item sx={{ position: 'relative' }}>
          <MapBase fillColor={props.themeInfo.outLineStyle.fillColor}></MapBase>
        </Grid>
        <Grid
          item
          sx={{ position: 'absolute' }}
          onClick={() => props.tileClickHandler(DamagedPartCodes.TRUNK)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              props.tileClickHandler(DamagedPartCodes.TRUNK);
            }
          }}
        >
          <DamagedPartTile
            damagePartCode={DamagedPartCodes.TRUNK}
            themeStyleInfo={props.themeInfo}
            isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.TRUNK)}
            isMobile={props.isMobile}
          ></DamagedPartTile>
        </Grid>
        <Grid
          item
          sx={{ position: 'absolute' }}
          onClick={() => props.tileClickHandler(DamagedPartCodes.REAR_WIND_SHIELD)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              props.tileClickHandler(DamagedPartCodes.REAR_WIND_SHIELD);
            }
          }}
        >
          <DamagedPartTile
            damagePartCode={DamagedPartCodes.REAR_WIND_SHIELD}
            themeStyleInfo={props.themeInfo}
            isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.REAR_WIND_SHIELD)}
            isMobile={props.isMobile}
          ></DamagedPartTile>
        </Grid>
        <Grid
          item
          sx={{ position: 'absolute' }}
          onClick={() => props.tileClickHandler(DamagedPartCodes.ROOF)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              props.tileClickHandler(DamagedPartCodes.ROOF);
            }
          }}
        >
          <DamagedPartTile
            damagePartCode={DamagedPartCodes.ROOF}
            themeStyleInfo={props.themeInfo}
            isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.ROOF)}
            isMobile={props.isMobile}
          ></DamagedPartTile>
        </Grid>
        <Grid
          item
          sx={{ position: 'absolute' }}
          onClick={() => props.tileClickHandler(DamagedPartCodes.FRONT_WINDSHIELD)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              props.tileClickHandler(DamagedPartCodes.FRONT_WINDSHIELD);
            }
          }}
        >
          <DamagedPartTile
            damagePartCode={DamagedPartCodes.FRONT_WINDSHIELD}
            themeStyleInfo={props.themeInfo}
            isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.FRONT_WINDSHIELD)}
            isMobile={props.isMobile}
          ></DamagedPartTile>
        </Grid>
        <Grid
          item
          sx={{ position: 'absolute' }}
          onClick={() => props.tileClickHandler(DamagedPartCodes.HOOD)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              props.tileClickHandler(DamagedPartCodes.HOOD);
            }
          }}
        >
          <DamagedPartTile
            damagePartCode={DamagedPartCodes.HOOD}
            themeStyleInfo={props.themeInfo}
            isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.HOOD)}
            isMobile={props.isMobile}
          ></DamagedPartTile>
        </Grid>
      </Grid>
      <Grid
        item
        id="bottom-center"
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.REAR_BUMPER)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.REAR_BUMPER);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.REAR_BUMPER}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.REAR_BUMPER)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
    </Grid>
  );
}

function DamageMapRightSection(props: DamagePartTileInteraction) {
  return (
    <Grid item container direction="column" spacing={3} xs={3} alignItems="center">
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.RIGHT_FRONT_WHEEL)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.RIGHT_FRONT_WHEEL);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.RIGHT_FRONT_WHEEL}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.RIGHT_FRONT_WHEEL)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.RIGHT_FENDER)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.RIGHT_FENDER);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.RIGHT_FENDER}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.RIGHT_FENDER)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.RIGHT_FRONT_DOOR)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.RIGHT_FRONT_DOOR);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.RIGHT_FRONT_DOOR}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.RIGHT_FRONT_DOOR)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>

      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.RIGHT_REAR_DOOR)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.RIGHT_REAR_DOOR);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.RIGHT_REAR_DOOR}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.RIGHT_REAR_DOOR)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.RIGHT_QUARTER_PANEL)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.RIGHT_QUARTER_PANEL);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.RIGHT_QUARTER_PANEL}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.RIGHT_QUARTER_PANEL)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
      <Grid
        item
        sx={{ position: 'relative' }}
        onClick={() => props.tileClickHandler(DamagedPartCodes.RIGHT_REAR_WHEEL)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.tileClickHandler(DamagedPartCodes.RIGHT_REAR_WHEEL);
          }
        }}
      >
        <DamagedPartTile
          damagePartCode={DamagedPartCodes.RIGHT_REAR_WHEEL}
          themeStyleInfo={props.themeInfo}
          isSelected={props.currentSelectionStateRequestHandler(DamagedPartCodes.RIGHT_REAR_WHEEL)}
          isMobile={props.isMobile}
        ></DamagedPartTile>
      </Grid>
    </Grid>
  );
}
